import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTransient } from '../lib/useTransient';

/** What a redirect attaches to say why it happened. */
export interface RouteNotice {
  notice?: string;
}

/**
 * The brief message a redirect leaves behind.
 *
 * Carried in router state rather than a global store: the only thing that
 * raises one is a redirect away from a page the account may not open, and one
 * field on the navigation is far less machinery than a provider wrapping the
 * app. Rendered once at the top of the tree, so it survives whichever route the
 * redirect lands on.
 */
export default function RouteToast() {
  const location = useLocation();
  const navigate = useNavigate();
  const notice = (location.state as RouteNotice | null)?.notice;
  const [message, setMessage] = useTransient<string | null>(null);

  useEffect(() => {
    if (!notice) return;
    setMessage(notice);
    /*
      Drop it from history immediately. Otherwise refreshing the page — or
      coming back to it later — replays a message about something that happened
      once, minutes ago. Clearing it also ends this effect, since `notice` is
      then undefined.
    */
    navigate(location.pathname, { replace: true, state: null });
  }, [notice, location.pathname, navigate, setMessage]);

  if (!message) return null;

  return (
    // Top of the viewport, in the same red the admin error banners use, so a
    // refusal looks like a refusal wherever it appears.
    /*
      z-[100], above everything else on the site. The public header is sticky at
      z-50 and renders after this in the tree, so at the same level it would
      paint straight over a toast sitting in its band.
    */
    <div
      role="alert"
      aria-live="assertive"
      className="fixed inset-x-0 top-6 z-[100] flex justify-center px-4"
    >
      <p className="rounded-lg border border-red-200 bg-red-50 px-5 py-3 text-sm font-medium text-red-800 shadow-lg">
        {message}
      </p>
    </div>
  );
}
